/**
 * PostImmersionDecisionModal — lets a pre-recruteur / referent / admin act on
 * an immersion that has happened (or should have). Two outcomes:
 *
 *   • "Immersion réussie" → status=validated (POST /candidates/:id/validate)
 *   • "Immersion non concluante" → status=rejected (POST /candidates/:id/reject)
 *
 * Each path requires picking a categorised reason and lets the user add a
 * free-form comment. Both are persisted in notes + status_history so the team
 * has full traceability of who/when/why.
 *
 * After save:
 *   - Calendar entry turns green-solid "FAIT" (validated) or red-barred
 *     "Refusé" (rejected) — handled by the backend's _status_kind.
 *   - Parent callback receives the updated candidate dict so it can refresh.
 */
import { motion } from "framer-motion";
import {
    CheckCircle2,
    ChevronLeft,
    Loader2,
    X,
    XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


const VALIDATE_REASONS = [
    { value: "profil_ideal", label: "Profil idéal pour la mission" },
    { value: "bonne_posture_terrain", label: "Bonne posture terrain" },
    { value: "validation_directe", label: "Validation directe sans réserve" },
    { value: "autre", label: "Autre (préciser dans le commentaire)" },
];

const REJECT_REASONS = [
    { value: "immersion_inadequat", label: "Inadapté au terrain" },
    { value: "immersion_comportement", label: "Comportement inadéquat" },
    { value: "immersion_no_show", label: "Le candidat ne s'est pas présenté" },
    { value: "immersion_candidat_retire", label: "Le candidat s'est retiré" },
    { value: "criteres_non_remplis", label: "Critères non remplis" },
    { value: "autre", label: "Autre (préciser dans le commentaire)" },
];


export default function PostImmersionDecisionModal({ candidate, onClose, onDone }) {
    // null = pick decision · "validated" / "rejected" = filling reason
    const [decision, setDecision] = useState(null);
    const [reason, setReason] = useState("");
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const name = `${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim() || "ce candidat";

    async function submit() {
        if (!decision || !reason || submitting) return;
        setSubmitting(true);
        try {
            if (decision === "validated") {
                const { data } = await api.post(`/candidates/${candidate.id}/validate`, {
                    reason,
                    comment: comment.trim() || null,
                });
                toast.success(`${name} validé(e) — recrutement acté`);
                onDone?.(data, "validated");
            } else {
                const { data } = await api.post(`/candidates/${candidate.id}/reject`, {
                    reason,
                    note: comment.trim() || null,
                });
                toast.success(`${name} refusé(e) après immersion`);
                onDone?.(data, "rejected");
            }
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
        } finally {
            setSubmitting(false);
        }
    }

    const reasons = decision === "validated" ? VALIDATE_REASONS : REJECT_REASONS;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={onClose}
            data-testid="post-immersion-decision-modal"
        >
            <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-shariti-gray flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {decision && (
                            <button
                                type="button"
                                onClick={() => { setDecision(null); setReason(""); setComment(""); }}
                                data-testid="decision-back"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-600 hover:bg-shariti-orange-soft hover:text-shariti-orange transition"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                        )}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-shariti-orange">
                                Décision post-immersion
                            </div>
                            <div className="text-sm font-extrabold text-shariti-black mt-0.5">
                                {name}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="decision-close"
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {!decision && (
                        <>
                            <p className="text-sm font-medium text-slate-600 mb-4">
                                Quelle est l&apos;issue de l&apos;immersion de <strong className="text-shariti-black">{name}</strong> ?
                            </p>
                            <div className="space-y-3">
                                <button
                                    type="button"
                                    onClick={() => setDecision("validated")}
                                    data-testid="decision-validate"
                                    className="w-full text-left rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition p-4 group"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-11 w-11 rounded-2xl bg-emerald-500 text-white inline-flex items-center justify-center shrink-0 shadow-btn">
                                            <CheckCircle2 className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-extrabold text-emerald-900">
                                                Immersion réussie — Candidat retenu
                                            </div>
                                            <div className="text-xs text-emerald-700 mt-0.5">
                                                Le candidat passe en <strong>Validé</strong> et entre dans le pipeline d&apos;embauche.
                                            </div>
                                        </div>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setDecision("rejected")}
                                    data-testid="decision-reject"
                                    className="w-full text-left rounded-2xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-400 transition p-4 group"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-11 w-11 rounded-2xl bg-shariti-red text-white inline-flex items-center justify-center shrink-0 shadow-btn">
                                            <XCircle className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-extrabold text-red-900">
                                                Immersion non concluante — Refuser
                                            </div>
                                            <div className="text-xs text-red-700 mt-0.5">
                                                Le candidat passe en <strong>Refusé</strong> avec motif tracé dans l&apos;historique.
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </>
                    )}

                    {decision && (
                        <>
                            <div className={`rounded-2xl border-2 p-3 mb-4 ${decision === "validated"
                                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                : "bg-red-50 border-red-200 text-red-900"
                                }`}>
                                <div className="flex items-center gap-2 text-sm font-extrabold">
                                    {decision === "validated"
                                        ? <CheckCircle2 className="h-4 w-4" />
                                        : <XCircle className="h-4 w-4" />}
                                    {decision === "validated"
                                        ? "Immersion réussie — Candidat retenu"
                                        : "Immersion non concluante — Refuser"}
                                </div>
                                {decision === "validated" && (
                                    <div className="mt-1.5 text-[11px] font-semibold text-emerald-800">
                                        Le dossier d'embauche numérique sera envoyé automatiquement à {candidate.first_name} par email dès validation.
                                    </div>
                                )}
                            </div>

                            <label className="block text-xs font-extrabold uppercase tracking-widest text-slate-600 mb-2">
                                Motif <span className="text-shariti-red">*</span>
                            </label>
                            <div className="space-y-1.5 mb-4">
                                {reasons.map((r) => (
                                    <label
                                        key={r.value}
                                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${reason === r.value
                                            ? decision === "validated"
                                                ? "bg-emerald-50 border-emerald-400"
                                                : "bg-red-50 border-red-400"
                                            : "bg-white border-shariti-gray hover:border-shariti-orange/40"
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name="post-immersion-reason"
                                            value={r.value}
                                            checked={reason === r.value}
                                            onChange={(e) => setReason(e.target.value)}
                                            data-testid={`reason-${r.value}`}
                                            className="accent-shariti-orange"
                                        />
                                        <span className="text-sm font-bold text-shariti-black">{r.label}</span>
                                    </label>
                                ))}
                            </div>

                            <label className="block text-xs font-extrabold uppercase tracking-widest text-slate-600 mb-2">
                                Commentaire {decision === "validated" ? "(optionnel)" : "(optionnel mais recommandé)"}
                            </label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                data-testid="decision-comment"
                                placeholder={decision === "validated"
                                    ? "Ex: A montré une excellente capacité d'écoute lors des échanges donateurs."
                                    : "Ex: Manque de constance et difficulté à respecter le pitch malgré les rappels."}
                                rows={4}
                                className="w-full rounded-xl border-2 border-shariti-gray px-3 py-2 text-sm focus:outline-none focus:border-shariti-orange transition resize-none"
                            />
                        </>
                    )}
                </div>

                {/* Footer */}
                {decision && (
                    <div className="px-5 py-4 border-t border-shariti-gray flex gap-2 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-4 h-10 rounded-full text-sm font-bold text-slate-600 hover:bg-shariti-cream transition disabled:opacity-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!reason || submitting}
                            data-testid="decision-submit"
                            className={`px-5 h-10 rounded-full text-sm font-extrabold text-white shadow-btn transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${decision === "validated"
                                ? "bg-emerald-500 hover:bg-emerald-600"
                                : "bg-shariti-red hover:bg-red-700"
                                }`}
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {decision === "validated"
                                ? "Confirmer la validation"
                                : "Confirmer le refus"}
                        </button>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
}
